"use client"

import type React from "react"
import { useEffect } from "react"
import { motion } from "framer-motion"
import type { SnackbarType } from "@/context/SnackbarContext"

interface CompactSnackbarProps {
  message: string;
  type: SnackbarType;
  duration?: number;
  delay?: number;
  theme?: "light" | "dark";
  index: number;
  onClose: () => void;
}

const CompactSnackbar: React.FC<CompactSnackbarProps> = ({
  message,
  type,
  duration = 4000,
  delay = 0,
  theme = "light",
  index,
  onClose,
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose()
    }, delay + duration)

    return () => clearTimeout(timer)
  }, [delay, duration, onClose])

  const getTypeConfig = () => {
    const configs = {
      success: {
        colors: {
          light: {
            bg: "bg-gradient-to-r from-emerald-50/95 to-green-50/95",
            border: "border-emerald-200/60",
            text: "text-emerald-800",
            icon: "bg-gradient-to-r from-emerald-500 to-green-500",
            glow: "shadow-emerald-500/20",
          },
          dark: {
            bg: "bg-gradient-to-r from-emerald-900/90 to-green-900/90",
            border: "border-emerald-700/50",
            text: "text-emerald-100",
            icon: "bg-gradient-to-r from-emerald-400 to-green-400",
            glow: "shadow-emerald-500/25",
          },
        },
        icon: (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        ),
      },
      warning: {
        colors: {
          light: {
            bg: "bg-gradient-to-r from-amber-50/95 to-orange-50/95",
            border: "border-amber-200/60",
            text: "text-amber-800",
            icon: "bg-gradient-to-r from-amber-500 to-orange-500",
            glow: "shadow-amber-500/20",
          },
          dark: {
            bg: "bg-gradient-to-r from-amber-900/90 to-orange-900/90",
            border: "border-amber-700/50",
            text: "text-amber-100",
            icon: "bg-gradient-to-r from-amber-400 to-orange-400",
            glow: "shadow-amber-500/25",
          },
        },
        icon: (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
      },
      info: {
        colors: {
          light: {
            bg: "bg-gradient-to-r from-blue-50/95 to-cyan-50/95",
            border: "border-blue-200/60",
            text: "text-blue-800",
            icon: "bg-gradient-to-r from-blue-500 to-cyan-500",
            glow: "shadow-blue-500/20",
          },
          dark: {
            bg: "bg-gradient-to-r from-blue-900/90 to-cyan-900/90",
            border: "border-blue-700/50",
            text: "text-blue-100",
            icon: "bg-gradient-to-r from-blue-400 to-cyan-400",
            glow: "shadow-blue-500/25",
          },
        },
        icon: (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
      },
      error: {
        colors: {
          light: {
            bg: "bg-gradient-to-r from-red-50/95 to-rose-50/95",
            border: "border-red-200/60",
            text: "text-red-800",
            icon: "bg-gradient-to-r from-red-500 to-rose-500",
            glow: "shadow-red-500/20",
          },
          dark: {
            bg: "bg-gradient-to-r from-red-900/90 to-rose-900/90",
            border: "border-red-700/50",
            text: "text-red-100",
            icon: "bg-gradient-to-r from-red-400 to-rose-400",
            glow: "shadow-red-500/25",
          },
        },
        icon: (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ),
      },
    }
    return configs[type]
  }

  const config = getTypeConfig()
  const colors = config.colors[theme]

  return (
    <motion.div
      layout
      initial={{
        x: 350,
        opacity: 0,
        scale: 0.9,
        rotateY: -10,
      }}
      animate={{
        x: 0,
        opacity: 1,
        scale: 1,
        rotateY: 0,
      }}
      exit={{
        x: 350,
        opacity: 0,
        scale: 0.9,
        rotateY: 10,
        transition: {
          duration: 0.3,
          ease: "easeInOut",
        },
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 25,
        delay: index * 0.1,
      }}
      className="pointer-events-auto"
    >
      <motion.div
        className={`
          ${colors.bg}
          ${colors.border}
          backdrop-blur-xl
          border
          rounded-xl
          shadow-lg
          ${colors.glow}
          px-3 py-2
          flex
          items-center
          gap-2.5
          min-w-[280px]
          max-w-[350px]
          relative
          overflow-hidden
        `}
        whileHover={{
          scale: 1.02,
          transition: { duration: 0.2 },
        }}
      >
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/15 via-white/5 to-transparent rounded-xl" />
        <div className="absolute inset-[0.5px] rounded-xl border border-white/20" />

        {/* Animated Icon */}
        <motion.div
          className={`
            w-7 h-7
            rounded-full
            flex items-center justify-center
            ${colors.icon}
            text-white
            shadow-sm
            flex-shrink-0
            relative
            z-50
          `}
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 20,
            delay: 0.1 + index * 0.05,
          }}
        >
          {/* Icon glow effect */}
          <motion.div
            className="absolute inset-0 rounded-full bg-white/20"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.1, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 0.5,
            }}
          />
          {config.icon}
        </motion.div>

        {/* Message */}
        <motion.div
          className="flex-1 relative z-50"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            delay: 0.2 + index * 0.05,
            duration: 0.4,
          }}
        >
          <p className={`text-xs font-medium ${colors.text} leading-tight`}>{message}</p>
        </motion.div>

        {/* Close Button */}
        <motion.button
          onClick={onClose}
          className={`
            ${colors.text}
            hover:bg-white/15
            transition-all
            duration-200
            p-1
            rounded-md
            relative
            z-50
            opacity-60
            hover:opacity-100
            flex-shrink-0
          `}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.6, scale: 1 }}
          whileHover={{
            opacity: 1,
            scale: 1.1,
            backgroundColor: "rgba(255, 255, 255, 0.15)",
          }}
          whileTap={{ scale: 0.9 }}
          transition={{ delay: 0.3 + index * 0.05 }}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </motion.button>

        {/* Progress indicator */}
        <motion.div
          className={`absolute bottom-0 left-0 h-0.5 ${colors.icon} rounded-full`}
          initial={{ width: "100%" }}
          animate={{ width: "0%" }}
          transition={{
            duration: duration / 1000,
            delay: delay / 1000,
            ease: "linear",
          }}
        />
      </motion.div>
    </motion.div>
  )
}

export type { SnackbarType };
export default CompactSnackbar
